/**
 * Only when the app runs on this computer: the emails it would have sent,
 * with their links, so sign-up and password reset can be tried without a
 * real mailbox. The API offers /api/dev/outbox only in that case (see
 * apps/api/src/app.ts); online this page just says there is nothing here.
 */
import { useEffect, useState } from 'react';
import { Link } from '../router.tsx';
import { t } from '../strings.ts';

interface DevMessage { to: string; subject: string; tag: string; link: string; sentAt: string }

async function fetchOutbox(): Promise<DevMessage[] | null> {
  try {
    const res = await fetch('/api/dev/outbox');
    return res.ok ? (await res.json()).messages : null;
  } catch {
    return null;
  }
}

/** A pointer to the local mailbox, shown only when there is one. */
export function DevMailboxHint() {
  const [available, setAvailable] = useState(false);
  useEffect(() => { fetchOutbox().then((m) => setAvailable(m !== null)); }, []);
  if (!available) return null;
  return <p className="note small">{t('devMailboxHint')} <Link href="/dev/mailbox">{t('devMailboxOpen')}</Link></p>;
}

export function MailboxPage() {
  const [messages, setMessages] = useState<DevMessage[] | null | undefined>(undefined);
  useEffect(() => {
    const load = () => fetchOutbox().then(setMessages);
    load();
    const timer = setInterval(load, 3000);
    return () => clearInterval(timer);
  }, []);
  return (
    <main className="page narrow">
      <p><Link href="/signin">← {t('signIn')}</Link></p>
      <h1>{t('devMailboxTitle')}</h1>
      <p className="muted">{t('devMailboxBody')}</p>
      {messages === undefined ? <p className="muted">{t('loading')}</p>
        : messages === null ? <p className="error">{t('devMailboxOff')}</p>
        : messages.length === 0 ? <p className="muted">{t('devMailboxEmpty')}</p> : (
          <ul className="mailbox">
            {messages.map((m) => (
              <li key={m.sentAt + m.to}>
                <div><strong>{m.subject}</strong><span className="muted small"> · {m.to} · {new Date(m.sentAt).toLocaleTimeString()}</span></div>
                <a href={m.link} className="button primary">{m.tag === 'verify-email' ? t('devConfirm') : t('devReset')}</a>
              </li>
            ))}
          </ul>
        )}
    </main>
  );
}
